import Fuse from 'fuse.js';

let fuse = null;
let searchData = [];

// Fuse.js configuration for both exact and fuzzy matching
const fuseOptions = {
  keys: [
    { name: 'title', weight: 0.3 },
    { name: 'content', weight: 0.4 },
    { name: 'keywords', weight: 0.2 },
    { name: 'sections', weight: 0.1 },
  ],
  threshold: 0.3, // More lenient for fuzzy matching
  distance: 100,
  minMatchCharLength: 1,
  includeScore: true,
  includeMatches: true,
  findAllMatches: true,
  ignoreLocation: true,
  useExtendedSearch: false,
};

// Initialize search with data
export const initializeSearch = async () => {
  try {
    console.log('Initializing search...');
    const response = await fetch('/search-index.json');
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    searchData = await response.json();
    console.log('Search data loaded:', searchData.length, 'items');
    fuse = new Fuse(searchData, fuseOptions);
    console.log('Fuse initialized successfully');
    return true;
  } catch (error) {
    console.error('Failed to initialize search:', error);
    return false;
  }
};

// Perform search with both exact and fuzzy matching
export const search = (query, limit = 8) => {
  if (!fuse || !query || query.trim().length < 1) {
    console.log('Search conditions not met:', {
      fuse: !!fuse,
      query,
      queryLength: query?.trim().length,
    });
    return [];
  }

  console.log('Performing search for:', query);

  // First try exact matches (case insensitive)
  const exactMatches = searchData.filter((item) => {
    const searchText =
      `${item.title} ${item.content} ${item.keywords.join(' ')} ${item.sections.join(' ')}`.toLowerCase();
    return searchText.includes(query.toLowerCase());
  });

  // Then get fuzzy matches
  const fuzzyResults = fuse.search(query, { limit });

  // Combine and deduplicate results with proper scoring
  const resultMap = new Map();

  // Add exact matches with high score (0.0 = perfect match)
  exactMatches.forEach((item) => {
    resultMap.set(item.id, { item, score: 0.0, type: 'exact' });
  });

  // Add fuzzy matches with their actual scores
  fuzzyResults.forEach(({ item, score }) => {
    if (!resultMap.has(item.id)) {
      resultMap.set(item.id, { item, score, type: 'fuzzy' });
    }
  });

  // Sort by score (lower score = better match in Fuse.js)
  const sortedResults = Array.from(resultMap.values()).sort((a, b) => a.score - b.score);

  console.log('Total results:', sortedResults.length, 'items');

  return sortedResults.slice(0, limit).map(({ item }) => {
    // Find the best matching snippet
    const bestSnippet = findBestSnippet(item, query);

    // Find the relevant section
    const relevantSection = findRelevantSection(item, query);

    return {
      id: item.id,
      title: item.title,
      url: item.url,
      type: item.type,
      snippet: bestSnippet,
      section: relevantSection,
    };
  });
};

// Find the best snippet that contains the search query
const findBestSnippet = (item, query) => {
  const queryWords = query
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word.length > 0);
  const content = item.content;

  // Find the best match in the content
  let bestMatch = null;
  let bestScore = 0;

  // Split content into sentences
  const sentences = content.split(/[.!?]+/).filter((s) => s.trim().length > 10);

  for (const sentence of sentences) {
    const sentenceLower = sentence.toLowerCase();
    let score = 0;

    // Calculate score based on how many query words are found
    queryWords.forEach((word) => {
      if (sentenceLower.includes(word)) {
        score += 1;
      }
    });

    // Prefer sentences with more query word matches
    if (score > bestScore) {
      bestScore = score;
      bestMatch = sentence.trim();
    }
  }

  // If we found a good match, use it
  if (bestMatch && bestScore > 0) {
    return createSnippet(bestMatch, queryWords, 200);
  }

  // Fallback: search for any occurrence of query words in content
  const contentLower = content.toLowerCase();
  for (const word of queryWords) {
    const index = contentLower.indexOf(word);
    if (index !== -1) {
      const start = Math.max(0, index - 50);
      const end = Math.min(content.length, index + word.length + 150);
      const snippet = content.substring(start, end);
      return createSnippet(snippet, queryWords, 200);
    }
  }

  // Final fallback - return beginning of content
  return createSnippet(content.substring(0, 200), queryWords, 200);
};

// Create a snippet with context around matched words
const createSnippet = (text, queryWords, maxLength = 200) => {
  let snippet = text;

  // If text is longer than maxLength, try to center it around query words
  if (snippet.length > maxLength) {
    const textLower = snippet.toLowerCase();
    let bestIndex = -1;

    // Find the first occurrence of any query word
    for (const word of queryWords) {
      const index = textLower.indexOf(word);
      if (index !== -1 && (bestIndex === -1 || index < bestIndex)) {
        bestIndex = index;
      }
    }

    if (bestIndex !== -1) {
      // Center the snippet around the matched word
      const start = Math.max(0, bestIndex - Math.floor(maxLength / 2));
      const end = Math.min(snippet.length, start + maxLength);
      snippet = snippet.substring(start, end);

      // Add ellipsis if we're not at the beginning/end
      if (start > 0) snippet = '...' + snippet;
      if (end < text.length) snippet = snippet + '...';
    } else {
      // Just truncate if no query words found
      snippet = snippet.substring(0, maxLength) + '...';
    }
  }

  return highlightMatches(snippet, queryWords);
};

// Find the relevant section (H2) for the search result
const findRelevantSection = (item, query) => {
  const queryWords = query
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word.length > 0);
  const content = item.content.toLowerCase();

  // Find the section that appears before the most relevant content
  for (let i = item.sections.length - 1; i >= 0; i--) {
    const section = item.sections[i];
    const sectionLower = section.toLowerCase();
    const sectionIndex = content.indexOf(sectionLower);

    if (sectionIndex !== -1) {
      // Check if query words appear after this section
      const textAfterSection = content.substring(sectionIndex);
      const hasQueryWords = queryWords.some((word) => textAfterSection.includes(word));

      if (hasQueryWords) {
        return section;
      }
    }
  }

  // Fallback to first section or return null
  return item.sections.length > 0 ? item.sections[0] : null;
};

// Highlight matching words in the snippet
const highlightMatches = (text, queryWords) => {
  let highlightedText = text;

  queryWords.forEach((word) => {
    if (word.length > 0) {
      const regex = new RegExp(`(${word})`, 'gi');
      highlightedText = highlightedText.replace(regex, '<strong>$1</strong>');
    }
  });

  // Ensure snippet isn't too long
  if (highlightedText.length > 300) {
    highlightedText = highlightedText.substring(0, 300) + '...';
  }

  return highlightedText;
};

// Get search suggestions (for autocomplete)
export const getSuggestions = (query, limit = 5) => {
  if (!fuse || !query || query.trim().length < 1) {
    return [];
  }

  const results = fuse.search(query, { limit });

  return results.map(({ item }) => ({
    id: item.id,
    title: item.title,
    url: item.url,
    type: item.type,
  }));
};

// Clear search (useful for cleanup)
export const clearSearch = () => {
  fuse = null;
  searchData = [];
};
